import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { instancesAPI } from '../../../lib/instancesApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  Package,
  ShoppingCart,
  DollarSign,
  Plus,
  Eye,
  Edit,
  Trash2,
  Crown,
  TrendingUp,
  Loader2,
} from 'lucide-react';

export default function Instances() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [instances, setInstances] = useState([]);
  const [masterStats, setMasterStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    if (user?.role === 'admin-master') {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [instancesRes, statsRes] = await Promise.all([
        instancesAPI.getAll(),
        instancesAPI.getMasterDashboard(),
      ]);
      setInstances(instancesRes.data);
      setMasterStats(statsRes.data);
    } catch (err) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInstance = async () => {
    try {
      await instancesAPI.create(formData);
      toast.success('Instância criada com sucesso');
      setShowForm(false);
      setFormData({ name: '', slug: '', email: '', phone: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleViewDetails = async (instance) => {
    try {
      const res = await instancesAPI.getById(instance.id);
      setSelectedInstance(res.data);
      setShowDetails(true);
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const getPlanBadge = (plan) => {
    const colors = {
      free: 'bg-gray-100 text-gray-800',
      basic: 'bg-blue-100 text-blue-800',
      pro: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-amber-100 text-amber-800',
    };
    return (
      <Badge className={colors[plan] || colors.free}>
        {t(`instances.plans.${plan}`)}
      </Badge>
    );
  };

  if (user?.role !== 'admin-master') {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <Crown className="h-12 w-12 mx-auto text-amber-500 mb-4" />
            <CardTitle>Acesso Restrito</CardTitle>
            <CardDescription>
              Esta área é exclusiva para Admin Master.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Crown className="h-6 w-6 text-amber-500" />
            {t('instances.masterDashboard')}
          </h1>
          <p className="text-muted-foreground">
            Gerencie todas as instâncias do sistema
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('instances.newInstance')}
        </Button>
      </div>

      {/* Stats Cards */}
      {masterStats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                {t('instances.totalInstances')}
              </CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{masterStats.total_instances}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                {t('instances.totalUsers')}
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{masterStats.total_users}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Total de Vendas
              </CardTitle>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{masterStats.total_sales}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                {t('instances.totalRevenue')}
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(masterStats.total_revenue)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Instances */}
      {masterStats?.top_instances?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {t('instances.topInstances')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {masterStats.top_instances.map((inst, idx) => (
                <div
                  key={inst.instance_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-muted-foreground">
                      #{idx + 1}
                    </span>
                    <div>
                      <p className="font-medium">{inst.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {inst.sales_count} vendas
                      </p>
                    </div>
                  </div>
                  <span className="font-bold text-green-600">
                    {formatCurrency(inst.total_sales)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instances Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('instances.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('instances.name')}</TableHead>
                <TableHead>{t('instances.slug')}</TableHead>
                <TableHead>{t('instances.plan')}</TableHead>
                <TableHead>{t('instances.stats.users')}</TableHead>
                <TableHead>{t('instances.stats.products')}</TableHead>
                <TableHead>{t('instances.stats.sales')}</TableHead>
                <TableHead>{t('instances.stats.totalRevenue')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {instances.map((instance) => (
                <TableRow key={instance.id}>
                  <TableCell className="font-medium">{instance.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {instance.slug}
                  </TableCell>
                  <TableCell>{getPlanBadge(instance.plan)}</TableCell>
                  <TableCell>{instance.stats?.users_count || 0}</TableCell>
                  <TableCell>{instance.stats?.products_count || 0}</TableCell>
                  <TableCell>{instance.stats?.sales_count || 0}</TableCell>
                  <TableCell className="font-medium">
                    {formatCurrency(instance.stats?.sales_total)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewDetails(instance)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Instance Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('instances.newInstance')}</DialogTitle>
            <DialogDescription>
              Crie uma nova instância para um restaurante
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('instances.name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Nome do restaurante"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">{t('instances.slug')}</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    slug: e.target.value.toLowerCase().replace(/\s+/g, '-'),
                  })
                }
                placeholder="meu-restaurante"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('instances.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="contato@restaurante.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t('instances.phone')}</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="(11) 99999-9999"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateInstance}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instance Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedInstance?.name}</DialogTitle>
            <DialogDescription>{selectedInstance?.slug}</DialogDescription>
          </DialogHeader>
          {selectedInstance && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 rounded-lg bg-muted">
                  <Users className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                  <p className="text-2xl font-bold">
                    {selectedInstance.stats?.users_count}
                  </p>
                  <p className="text-xs text-muted-foreground">Usuários</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted">
                  <Package className="h-5 w-5 mx-auto mb-1 text-green-500" />
                  <p className="text-2xl font-bold">
                    {selectedInstance.stats?.products_count}
                  </p>
                  <p className="text-xs text-muted-foreground">Produtos</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-muted">
                  <ShoppingCart className="h-5 w-5 mx-auto mb-1 text-purple-500" />
                  <p className="text-2xl font-bold">
                    {selectedInstance.stats?.sales_count}
                  </p>
                  <p className="text-xs text-muted-foreground">Vendas</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Categorias</p>
                  <p className="font-medium">
                    {selectedInstance.stats?.categories_count}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Clientes</p>
                  <p className="font-medium">
                    {selectedInstance.stats?.customers_count}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Mesas</p>
                  <p className="font-medium">
                    {selectedInstance.stats?.tables_count}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Perfis</p>
                  <p className="font-medium">
                    {selectedInstance.stats?.profiles_count}
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950">
                <p className="text-sm text-green-600 dark:text-green-400">
                  Receita Total
                </p>
                <p className="text-3xl font-bold text-green-700 dark:text-green-300">
                  {formatCurrency(selectedInstance.stats?.sales_total)}
                </p>
              </div>

              {/* Users List */}
              {selectedInstance.users?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Usuários</h4>
                  <div className="space-y-2">
                    {selectedInstance.users.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-2 rounded bg-muted"
                      >
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                        <Badge>{t(`users.roles.${user.role}`)}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
